// The reactive state written to by the MediaService
// to be reactively read by the React components.
// Notice, this is comparable to a set of rxjs BehaviorSubjects
// (https://rxjs.dev/api/index/class/BehaviorSubject) bound to
// Angular templates using an async pipe.
import { create } from 'zustand';
// Define the default values of the state.
// This is not exported on purpose to prevent misuse.
const useMediaOutput = create((_set) => ({
    mediaStreamData: {
        mediaStream: new MediaStream(),
        isUserFacingVideo: false,
        isSharingScreen: false,
        isSharingScreenAudio: false,
        isSharingScreenVideo: false,
        hasAudio: false,
        hasVideo: false,
        hasTracks: false,
    },
    mediaOutputVideoDevices: [],
    mediaOutputAudioDevices: [],
    mediaOutputConnectedVideoDevices: [],
    mediaOutputConnectedAudioDevices: [],
    mediaOutputSelectedVideoDevice: undefined,
    mediaOutputSelectedAudioDevice: undefined,
    mediaOutputMicrophonePermissioned: false,
    mediaOutputCameraPermissioned: false,
    mediaOutputAudioMuted: false,
    mediaOutputVideoMuted: false,
    mediaOutputMediaStreamHasTracks: false,
}));
// The rest of this file is AI-generated boilerplate, do not get intimidated
// by the length of the file.
// Reactive getters to be used in React
const reactiveOutput = {
    useMediaStreamData: () => {
        return useMediaOutput((state) => state.mediaStreamData);
    },
    useVideoDevices: () => {
        return useMediaOutput((state) => state.mediaOutputVideoDevices);
    },
    useAudioDevices: () => {
        return useMediaOutput((state) => state.mediaOutputAudioDevices);
    },
    useConnectedVideoDevices: () => {
        return useMediaOutput((state) => state.mediaOutputConnectedVideoDevices);
    },
    useConnectedAudioDevices: () => {
        return useMediaOutput((state) => state.mediaOutputConnectedAudioDevices);
    },
    useSelectedVideoDevice: () => {
        return useMediaOutput((state) => state.mediaOutputSelectedVideoDevice);
    },
    useSelectedAudioDevice: () => {
        return useMediaOutput((state) => state.mediaOutputSelectedAudioDevice);
    },
    useMicrophonePermissioned: () => {
        return useMediaOutput((state) => state.mediaOutputMicrophonePermissioned);
    },
    useCameraPermissioned: () => {
        return useMediaOutput((state) => state.mediaOutputCameraPermissioned);
    },
    useAudioMuted: () => {
        return useMediaOutput((state) => state.mediaOutputAudioMuted);
    },
    useVideoMuted: () => {
        return useMediaOutput((state) => state.mediaOutputVideoMuted);
    },
    useSelectedDevice: (kind) => {
        const audioDevice = useMediaOutput((state) => state.mediaOutputSelectedAudioDevice);
        const videoDevice = useMediaOutput((state) => state.mediaOutputSelectedVideoDevice);
        return kind === 'audio' ? audioDevice : videoDevice;
    },
};
// Setters to be used by the MediaService
export class MediaServiceOutput {
    // This class should only be used by the MediaService
    static getReactiveOutput() {
        return reactiveOutput;
    }
    setMediaStreamData(mediaStreamData) {
        useMediaOutput.setState({ mediaStreamData });
    }
    setVideoDevices(videoDevices) {
        useMediaOutput.setState({ mediaOutputVideoDevices: videoDevices });
    }
    setAudioDevices(audioDevices) {
        useMediaOutput.setState({ mediaOutputAudioDevices: audioDevices });
    }
    setConnectedVideoDevices(videoDevices) {
        useMediaOutput.setState({
            mediaOutputConnectedVideoDevices: videoDevices,
        });
    }
    setConnectedAudioDevices(audioDevices) {
        useMediaOutput.setState({
            mediaOutputConnectedAudioDevices: audioDevices,
        });
    }
    setSelectedVideoDevice(videoDevice) {
        useMediaOutput.setState({
            mediaOutputSelectedVideoDevice: videoDevice,
        });
    }
    setSelectedAudioDevice(audioDevice) {
        useMediaOutput.setState({
            mediaOutputSelectedAudioDevice: audioDevice,
        });
    }
    setMicrophonePermissioned(microphonePermissioned) {
        useMediaOutput.setState({
            mediaOutputMicrophonePermissioned: microphonePermissioned,
        });
    }
    setCameraPermissioned(cameraPermissioned) {
        useMediaOutput.setState({
            mediaOutputCameraPermissioned: cameraPermissioned,
        });
    }
    setAudioMuted(isAudioMuted) {
        useMediaOutput.setState({ mediaOutputAudioMuted: isAudioMuted });
    }
    setVideoMuted(isVideoMuted) {
        useMediaOutput.setState({ mediaOutputVideoMuted: isVideoMuted });
    }
}
